import { cn } from 'designSystem/lib/utils'
import {
  type FileUploadInput,
  flattenDataTransferFiles,
} from 'features/assetLifecycleV2/shared/fileUploadSelection'
import { UploadCloud } from 'lucide-react'
import * as React from 'react'

export interface FileDropZoneState {
  isDragging: boolean
  isPreparingFiles: boolean
}

export interface FileDropZoneProps {
  onFiles: (files: FileUploadInput[]) => void
  children: React.ReactNode | ((state: FileDropZoneState) => React.ReactNode)
  className?: string
}

/**
 * Wraps a region so that file drops within it are captured and forwarded via
 * `onFiles`.
 *
 * - **Simple form** (`children` is ReactNode): shows a drag overlay.
 * - **Render-prop form** (`children` is a function): passes `{ isDragging }`
 *   so the caller can control the visual treatment. No overlay is rendered.
 */
export function FileDropZone({
  onFiles,
  children,
  className,
}: FileDropZoneProps) {
  const dragCounterRef = React.useRef(0)
  const [isDragging, setIsDragging] = React.useState(false)
  const [isPreparingFiles, setIsPreparingFiles] = React.useState(false)

  const dragHasFiles = (e: React.DragEvent<HTMLDivElement>): boolean =>
    Array.from(e.dataTransfer.types ?? []).includes('Files')

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFiles(e)) return
    e.preventDefault()
    dragCounterRef.current += 1
    setIsDragging(true)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFiles(e)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFiles(e)) return
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1)
    if (dragCounterRef.current === 0) setIsDragging(false)
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFiles(e)) return
    e.preventDefault()
    const dataTransfer = e.dataTransfer
    dragCounterRef.current = 0
    setIsDragging(false)
    setIsPreparingFiles(true)
    try {
      const files = await flattenDataTransferFiles(dataTransfer)
      if (files.length > 0) onFiles(files)
    } finally {
      setIsPreparingFiles(false)
    }
  }

  const isRenderProp = typeof children === 'function'

  return (
    <div
      className={cn('relative', className)}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      {isRenderProp ? children({ isDragging, isPreparingFiles }) : children}
      {!isRenderProp && (isDragging || isPreparingFiles) && (
        <div
          className='pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-lg bg-primary/10 text-primary'
          aria-hidden>
          <UploadCloud className='size-8' />
          <p className='text-sm font-medium'>
            {isPreparingFiles ? 'Preparing files…' : 'Drop files to upload'}
          </p>
        </div>
      )}
    </div>
  )
}
